import { MODEL_NAMES } from '../../../constants/index';
import { service } from '@ember/service';
import { setOwner } from '@ember/application';

export default class WebsocketActivityHandlersMessage {
  @service messaging;
  @service messagingNavigation;
  @service messagingLogging;
  @service store;

  constructor(owner) {
    setOwner(this, owner);
  }

  handleActivity(response) {
    const statusHandlers = {
      created: this.#handleCreatedActivity,
      destroyed: this.#handleDestroyedActivity,
    };
    const handler = response.status ? statusHandlers[response.status] : null;

    if (!handler) {
      this.messagingLogging.warn('Unhandled websocket message activity status', {
        action: 'message',
        status: response.status,
      });
      return;
    }

    handler.call(this, response.payload);
  }

  #handleCreatedActivity(payload) {
    const message = this.store.normalize(MODEL_NAMES.MESSAGE, payload.data);
    const {
      data: { attributes, relationships },
    } = message;
    const conversationId = relationships.conversation.data.id;

    let localRecord;
    if (attributes.localId) {
      localRecord = this.store
        .peekAll(MODEL_NAMES.MESSAGE)
        .find(record => record.localId === attributes.localId);
    }
    const currentHandler = this.messagingNavigation.currentConversationHandler;
    const isFromCurrentConversation = currentHandler?.conversation?.id === conversationId;
    const record = localRecord || this.store.push(message);

    // Display order is owned by conversation.gjs (messageSections).
    // Live rows just unshift onto the open-thread infinity list.
    if (isFromCurrentConversation && !this.#messageAlreadyPresent(currentHandler, record)) {
      currentHandler.messages?.unshiftObject(record);
    }

    if (!attributes.isSystem) {
      this.#updateConversationLastMessage(conversationId, record);
      if (!attributes.isOwn) {
        this.#addUnreadConversation(conversationId);
      }
    }
  }

  #handleDestroyedActivity(payload) {
    this.store.pushPayload(MODEL_NAMES.MESSAGE, payload);
  }

  // Only treat id / localId as duplicates when both sides have a real value.
  // Bare `null === null` / `undefined === undefined` would skip every system row
  // (and other server messages without localId) once any such row is in the list.
  #messageAlreadyPresent(handler, message) {
    return handler.messages?.content?.some(m => {
      if (message.id !== null && message.id !== undefined && m.id === message.id) {
        return true;
      }
      if (message.localId && m.localId === message.localId) {
        return true;
      }
      return false;
    });
  }

  #updateConversationLastMessage(conversationId, message) {
    const conversation = this.store.peekRecord(MODEL_NAMES.CONVERSATION, conversationId);
    // The list can hold a different object for this id if a socket push raced
    // the create `save()`. Promote it too, or the row stays a draft with no
    // last message and reopening it brings the draft banner back.
    const listed = this.messaging.conversations?.content?.find(conv =>
      conv.isSameId?.(conversationId)
    );

    if (!conversation && !listed) {
      this.messagingLogging.warn('Conversation missing when updating last message', {
        conversationId,
      });
      return;
    }

    conversation?.applyCreatedMessage(message);
    if (listed && listed !== conversation) {
      listed.applyCreatedMessage(message);
    }
  }

  #addUnreadConversation(conversationId) {
    this.messaging.currentProfile.addUnreadConversation(conversationId);
  }
}
